import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Plus, Search } from 'lucide-react'
import { purgeOtherClients, useClients } from '@/api/queries'
import { PageHeader } from '@/components/app/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { DataTable } from '@/components/ui/DataTable'
import { Input } from '@/components/ui/Field'
import { EmptyState, ErrorState } from '@/components/ui/States'
import { useConfig } from '@/config/ConfigProvider'
import { buildColumns } from './columns'
import { partitionClients } from './order'
import { NewClientModal } from './NewClientModal'
import type { Client } from '@/types'

/**
 * Clients are provisioned out of band (docs/00-overview.md §4). The "New
 * client" button is a temporary exception for testing and setup (APP-71),
 * shown only when config.json sets `features.clientCreate`.
 *
 * Two lists: crawled clients, most recent first, then the ones never crawled
 * (order.ts).
 */
export function ClientsPage() {
  const { data: clients = [], isLoading, isError, refetch } = useClients()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [query, setQuery] = useState('')
  const [creating, setCreating] = useState(false)
  const { features } = useConfig()

  const { crawled, notCrawled } = useMemo(() => partitionClients(clients, query), [clients, query])

  function open(client: Client) {
    // Drop every other tenant's cache before the scoped routes mount.
    purgeOtherClients(qc, client.id)
    localStorage.setItem('wbm.lastClientId', client.id)
    navigate(`/c/${client.id}/crawl`)
  }

  const columns = buildColumns({ onOpen: open })

  return (
    <>
      <PageHeader
        title="Clients"
        description="Pick a client to work in. Every review surface is scoped to the client in the URL."
        actions={
          features.clientCreate && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus size={14} aria-hidden />
              New client
            </Button>
          )
        }
      />

      {features.clientCreate && (
        <NewClientModal
          open={creating}
          onClose={() => setCreating(false)}
          onCreated={(client) => {
            setCreating(false)
            open(client)
          }}
        />
      )}

      <Card className="overflow-hidden">
        <div className="px-4 py-3">
          <div className="relative w-64">
            <Search
              size={13}
              aria-hidden
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint"
            />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search clients"
              aria-label="Search clients"
              className="h-8 pl-7 text-[13px]"
            />
          </div>
        </div>

        {isError ? (
          <ErrorState
            message="The client list did not load. This is usually a session problem rather than a permissions one."
            onRetry={() => void refetch()}
          />
        ) : (
          <>
            {(isLoading || crawled.length > 0 || notCrawled.length === 0) && (
              <DataTable
                rows={crawled}
                columns={columns}
                rowKey={(c) => c.id}
                loading={isLoading}
                onRowClick={open}
                empty={
                  <EmptyState
                    title={query ? 'No clients match that search' : 'No clients provisioned yet'}
                    description={
                      query
                        ? undefined
                        : 'Clients are provisioned outside the portal. Ask an administrator to set one up and grant you access.'
                    }
                  />
                }
              />
            )}
            {!isLoading && notCrawled.length > 0 && (
              <section aria-labelledby="not-crawled-heading" className="border-t border-rule">
                <h2 id="not-crawled-heading" className="field-label px-4 pb-1 pt-4">
                  Not crawled yet ({notCrawled.length})
                </h2>
                <DataTable rows={notCrawled} columns={columns} rowKey={(c) => c.id} onRowClick={open} />
              </section>
            )}
          </>
        )}
      </Card>
    </>
  )
}
